import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ToastProvider } from '@core';
import { CasesRepository } from '../../../infrastructure/repositories/cases.repository';
import { CasesState } from '../../states/cases.state';
import { CloseCaseCommand } from './close-case.command';
import { CloseCaseResult } from './close-case.result';

/**
 * Close Case Use Case
 *
 * Closes a case with mandatory closing reason and notes.
 * Triggers when user changes status to Closed/Resolved category.
 *
 * Side effects:
 * - Updates case state
 * - Shows success toast
 * - Triggers case detail reload (handled by component)
 * - [Future] Flags AI summary as stale
 */
@Injectable({ providedIn: 'root' })
export class CloseCaseUseCase {
  private readonly _repository = inject(CasesRepository);
  private readonly _state = inject(CasesState);
  private readonly _toast = inject(ToastProvider);

  /**
   * Execute close case operation
   * @param command - Close case command with reason and notes
   * @returns Observable with result
   */
  public execute(command: CloseCaseCommand): Observable<CloseCaseResult> {
    // Set loading state
    this._state.setClosingCase(true);
    this._state.setErrorClosingCase(null);

    return this._repository
      .closeCase(command.caseId, {
        statusId: command.statusId,
        closingReasonId: command.closingReasonId,
        closingNotes: command.closingNotes,
      })
      .pipe(
        tap(() => {
          // Success: update state
          this._state.setClosingCase(false);

          // Show success toast
          this._toast.success(
            'Case closed',
            'The case has been closed successfully with closing notes recorded.'
          );

          // TODO(HCMI-444): Flag AI summary as stale when feature is implemented
        }),
        map(() => ({
          success: true,
          caseId: command.caseId,
        })),
        catchError((error) => {
          // Error: update state
          this._state.setErrorClosingCase(error.message || 'Failed to close case');
          this._state.setClosingCase(false);

          // Show error toast
          this._toast.error(
            'Failed to close case',
            'Please try again or contact support if the issue persists.'
          );

          return throwError(() => error);
        })
      );
  }
}
